import { useCallback, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import InputAdornment from "@mui/material/InputAdornment";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
    ArrowRight,
    CaseMinimalistic,
    Document,
    Magnifier,
    User,
    Shop,
} from "@solar-icons/react/ssr";
import { useSelector } from "react-redux";

import { request } from "@/Api";
import { InternalLink } from "@/common/CommonComponents";
import {
    desktopTopNavActionsFallbackWidth,
    desktopTopNavActionsGap,
    desktopTopNavHorizontalPadding,
    sideNavCollapsedHoverOffset,
    sideNavCollapsedWidth,
    sideNavHoverOffset,
    sideNavTransitionDuration,
    sideNavTransitionEasing,
    sideNavWidth,
} from "@/nav/navConstants";
import { NavItemIcon } from "@/nav/NavIcons";
import { selectNavToggleHovered } from "@/nav/navSlice";
import { colorPalette } from "@/themes";

const resultOpacityDuration = 200;
const globalSearchInputId = "global-search-input";
const globalSearchResultsId = "global-search-results";

const hasSearchResults = (result) =>
    Boolean(result) &&
    ["projects", "bids", "persons", "clients"].some(
        (key) => result[key]?.length > 0,
    );

export default function GlobalSearch({
    isNavOpen = false,
    rightActionsWidth = desktopTopNavActionsFallbackWidth,
}) {
    const latestSearchRequestRef = useRef(0);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [searchString, setSearchString] = useState("");
    const [searchResult, setSearchResult] = useState(null);
    const isNavToggleHovered = useSelector(selectNavToggleHovered);
    const isSearchActive = isSearchFocused || Boolean(searchString);
    const visibleNavWidth =
        (isNavOpen ? sideNavWidth : sideNavCollapsedWidth) - 10;
    const navDelta = isNavToggleHovered
        ? isNavOpen
            ? -sideNavHoverOffset
            : sideNavCollapsedHoverOffset
        : 0;
    const searchMarginLeft = Math.max(0, visibleNavWidth + navDelta);
    const searchRightReservedWidth =
        (rightActionsWidth || desktopTopNavActionsFallbackWidth) +
        desktopTopNavHorizontalPadding * 2 +
        desktopTopNavActionsGap;

    useEffect(() => {
        const requestId = latestSearchRequestRef.current + 1;
        latestSearchRequestRef.current = requestId;
        const query = searchString.trim();

        if (!query) {
            return undefined;
        }

        const debounceTimeout = setTimeout(() => {
            request
                .get("/search", { params: { search_string: query } })
                .then((response) => {
                    if (latestSearchRequestRef.current == requestId) {
                        setSearchResult(response.data);
                    }
                })
                .catch(() => {
                    if (latestSearchRequestRef.current == requestId) {
                        setSearchResult(null);
                    }
                });
        }, 250);

        return () => clearTimeout(debounceTimeout);
    }, [searchString]);

    const handleSearchChange = useCallback((event) => {
        const nextSearchString = event.target.value;
        setSearchString(nextSearchString);

        if (!nextSearchString.trim()) {
            latestSearchRequestRef.current += 1;
            setSearchResult(null);
        }
    }, []);

    const handleSearchBlur = useCallback(() => {
        if (!searchString) {
            setIsSearchFocused(false);
        }
    }, [searchString]);

    const handleSearchFocus = useCallback(() => {
        setIsSearchFocused(true);
    }, []);

    const handleCloseSearch = useCallback(() => {
        setSearchString("");
        setIsSearchFocused(false);
        setSearchResult(null);
        document.getElementById(globalSearchInputId)?.blur();
    }, []);

    return (
        <Box
            data-testid="global-search-container"
            role="search"
            aria-label="Global search"
            sx={{
                alignItems: "center",
                display: "flex",
                height: 51,
                marginLeft: `${searchMarginLeft}px`,
                maxWidth: `max(0px, calc(100vw - ${searchMarginLeft + searchRightReservedWidth}px))`,
                position: "absolute",
                top: 16.5,
                left: 50,
                transition: `margin-left ${sideNavTransitionDuration}ms ${sideNavTransitionEasing}`,
                width: 720,
            }}
        >
            <NavSearchBox
                active={isSearchActive}
                handleSearch={handleSearchChange}
                inputId={globalSearchInputId}
                onBlur={handleSearchBlur}
                onFocus={handleSearchFocus}
                resultsId={globalSearchResultsId}
                value={searchString}
            />
            <GlobalSearchResult
                isSearchActive={Boolean(searchString)}
                resultsId={globalSearchResultsId}
                searchResult={searchResult}
                onClose={handleCloseSearch}
            />
        </Box>
    );
}

const NavSearchBox = ({
    active,
    handleSearch,
    inputId,
    onBlur,
    onFocus,
    resultsId,
    value,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode == "dark";
    const inactiveColor = isDarkMode
        ? `${colorPalette.white}4D`
        : colorPalette.universalGrey;
    const iconColor = active && isDarkMode ? colorPalette.white : inactiveColor;

    return (
        <Box
            data-testid="global-search-box"
            sx={{
                alignItems: "center",
                background: theme.palette.background.paper,
                borderRadius: active ? "10000px" : "1000px",
                boxSizing: "border-box",
                display: "flex",
                flexShrink: 0,
                height: 40,
                maxWidth: "100%",
                overflow: "hidden",
                transition: theme.transitions.create(
                    ["width", "border-radius"],
                    {
                        easing: theme.transitions.easing.easeIn,
                        duration: 300,
                    },
                ),
                width: active ? 717 : 346,
            }}
        >
            <TextField
                autoComplete="off"
                id={inputId}
                inputProps={{
                    "aria-controls": resultsId,
                    "aria-label": "Global search",
                }}
                onBlur={onBlur}
                onChange={handleSearch}
                onFocus={onFocus}
                placeholder="Search..."
                size="small"
                value={value}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchBarIcon
                                color={iconColor}
                                icon={Magnifier}
                                size={14}
                            />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    height: "100%",
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                        alignItems: "center",
                        borderRadius: "inherit",
                        height: "40px",
                        padding: "0 18px",
                        "& fieldset": { border: "none" },
                    },
                    "& .MuiInputAdornment-root": {
                        color: iconColor,
                        height: 14,
                        marginLeft: 0,
                        marginRight: "10px",
                        maxHeight: "none",
                    },
                    "& .MuiInputAdornment-root svg": {
                        display: "block",
                        height: 14,
                        width: 14,
                    },
                    "& .MuiOutlinedInput-input": {
                        caretColor: theme.palette.text.primary,
                        color: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 14,
                        fontWeight: 400,
                        height: 17,
                        letterSpacing: "0px",
                        lineHeight: "100%",
                        padding: 0,
                    },
                    "& .MuiOutlinedInput-input::placeholder": {
                        color: inactiveColor,
                        opacity: 1,
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input::placeholder":
                        {
                            opacity: 0,
                        },
                }}
            />
        </Box>
    );
};

const SearchBarIcon = ({ color = "currentColor", icon, size = 13 }) => (
    <NavItemIcon color={color} icon={icon} size={size} />
);

const GlobalSearchResult = ({
    isSearchActive,
    resultsId,
    searchResult,
    onClose,
}) => {
    const [closedSearchResult, setClosedSearchResult] = useState(null);
    const [displayedSearchResult, setDisplayedSearchResult] = useState(null);
    const [isResultMounted, setIsResultMounted] = useState(false);
    const [isResultVisible, setIsResultVisible] = useState(false);
    const mountAnimationFrameRef = useRef(null);
    const visibilityAnimationFrameRef = useRef(null);
    const unmountTimeoutRef = useRef(null);
    const theme = useTheme();
    const activeResultHasResults = hasSearchResults(searchResult);
    const open =
        isSearchActive &&
        activeResultHasResults &&
        searchResult !== closedSearchResult;

    useEffect(() => {
        if (mountAnimationFrameRef.current) {
            cancelAnimationFrame(mountAnimationFrameRef.current);
        }
        if (visibilityAnimationFrameRef.current) {
            cancelAnimationFrame(visibilityAnimationFrameRef.current);
        }
        if (unmountTimeoutRef.current) {
            clearTimeout(unmountTimeoutRef.current);
        }

        if (open) {
            mountAnimationFrameRef.current = requestAnimationFrame(() => {
                setDisplayedSearchResult(searchResult);
                setIsResultMounted(true);
                visibilityAnimationFrameRef.current = requestAnimationFrame(
                    () => {
                        setIsResultVisible(true);
                    },
                );
            });
        } else {
            mountAnimationFrameRef.current = requestAnimationFrame(() => {
                setIsResultVisible(false);
                unmountTimeoutRef.current = setTimeout(() => {
                    setIsResultMounted(false);
                    if (!activeResultHasResults) {
                        setDisplayedSearchResult(null);
                    }
                }, resultOpacityDuration);
            });
        }

        return () => {
            if (mountAnimationFrameRef.current) {
                cancelAnimationFrame(mountAnimationFrameRef.current);
            }
            if (visibilityAnimationFrameRef.current) {
                cancelAnimationFrame(visibilityAnimationFrameRef.current);
            }
            if (unmountTimeoutRef.current) {
                clearTimeout(unmountTimeoutRef.current);
            }
        };
    }, [activeResultHasResults, open, searchResult]);

    if (!isResultMounted || !hasSearchResults(displayedSearchResult)) {
        return null;
    }
    const handleClickAway = () => {
        setClosedSearchResult(searchResult);
    };
    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box
                id={resultsId}
                role="region"
                aria-label="Global search results"
                zIndex={3}
                position={"absolute"}
                top={"55.5px"}
                left={0}
                width={"min(717px, 100%)"}
                maxHeight={"80vh"}
                sx={{
                    background: theme.palette.background.paper,
                    borderRadius: "16px",
                    boxShadow: `0px 0px 50px 10px ${colorPalette.black}40`,
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    gap: "19px",
                    opacity: isResultVisible ? 1 : 0,
                    overflowY: "auto",
                    padding: "20px 0",
                    pointerEvents: isResultVisible ? "auto" : "none",
                    transition: theme.transitions.create("opacity", {
                        easing: theme.transitions.easing.easeOut,
                        duration: resultOpacityDuration,
                    }),
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                    },
                }}
            >
                <ResultGroup
                    route={"projects"}
                    title={"Projects"}
                    entities={displayedSearchResult.projects}
                    icon={CaseMinimalistic}
                    onClose={onClose}
                />
                <ResultGroup
                    route={"bids"}
                    title={"Bid Sheets"}
                    entities={displayedSearchResult.bids}
                    icon={Document}
                    onClose={onClose}
                />
                <ResultGroup
                    route={"profile"}
                    title={"Users"}
                    entities={displayedSearchResult.persons}
                    icon={User}
                    onClose={onClose}
                />
                <ResultGroup
                    route={"clients"}
                    title={"Clients"}
                    entities={displayedSearchResult.clients}
                    icon={CaseMinimalistic}
                    onClose={onClose}
                />
                <ResultGroup
                    route={"vendors"}
                    title={"Vendors"}
                    entities={displayedSearchResult.vendors}
                    icon={Shop}
                    iconColor={"#80C8FF"}
                />
            </Box>
        </ClickAwayListener>
    );
};

const ResultGroup = ({ route, entities = [], title, icon, onClose }) => {
    const theme = useTheme();
    const hoverBackgroundColor =
        theme.palette.mode == "dark"
            ? colorPalette.grey.dark.two
            : colorPalette.grey.light.two;
    const hoverArrowColor =
        theme.palette.mode == "dark" ? colorPalette.white : colorPalette.black;
    if (entities.length == 0) {
        return null;
    }
    return (
        <Box
            data-testid={`global-search-results-${route}`}
            sx={{
                boxSizing: "border-box",
                padding: "0 20px",
                width: "100%",
            }}
        >
            <Typography
                sx={{
                    color: colorPalette.universalGrey,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 12,
                    fontWeight: 400,
                    letterSpacing: "0px",
                    lineHeight: "100%",
                    marginBottom: "7px",
                }}
            >
                {title}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                }}
            >
                {entities.map((entity, i) => (
                    <InternalLink
                        key={entity.id || `${route}-${entity.name}-${i}`}
                        href={`/${route}/${entity.id}`}
                        aria-label={`Open ${title} ${entity.name}`}
                        underline="none"
                        onClick={onClose}
                        sx={{
                            alignItems: "center",
                            borderRadius: "5px",
                            boxSizing: "border-box",
                            color: theme.palette.text.primary,
                            display: "flex",
                            justifyContent: "space-between",
                            minHeight: 29,
                            minWidth: 0,
                            padding: "6px 4px",
                            pointerEvents: entity.id ? "all" : "none",
                            textAlign: "left",
                            transform: "translateY(0px)",
                            transition: theme.transitions.create(
                                ["background-color", "color", "transform"],
                                {
                                    easing: theme.transitions.easing.easeOut,
                                    duration: 200,
                                },
                            ),
                            width: "100%",
                            "&:hover": {
                                backgroundColor: hoverBackgroundColor,
                                transform: "translateY(-1px)",
                            },
                            "&:hover .global-search-result-arrow": {
                                color: hoverArrowColor,
                                opacity: 1,
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                alignItems: "center",
                                display: "flex",
                                gap: "7px",
                                height: 17,
                                minWidth: 0,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: "flex",
                                    flexShrink: 0,
                                    lineHeight: 0,
                                }}
                            >
                                <SearchBarIcon icon={icon} />
                            </Box>
                            <Typography
                                component="span"
                                sx={{
                                    color: "inherit",
                                    display: "flex",
                                    alignItems: "center",
                                    flex: 1,
                                    fontFamily: theme.typography.fontFamily,
                                    fontSize: 14,
                                    fontWeight: 400,
                                    height: 17,
                                    letterSpacing: "0px",
                                    lineHeight: "17px",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {entity.name}
                            </Typography>
                        </Box>
                        {entity.id ? (
                            <Box
                                className="global-search-result-arrow"
                                component="span"
                                sx={{
                                    color: hoverArrowColor,
                                    display: "flex",
                                    flexShrink: 0,
                                    lineHeight: 0,
                                    opacity: 0,
                                    transition: theme.transitions.create(
                                        "opacity",
                                        {
                                            easing: theme.transitions.easing
                                                .easeOut,
                                            duration: 200,
                                        },
                                    ),
                                }}
                            >
                                <SearchBarIcon icon={ArrowRight} />
                            </Box>
                        ) : null}
                    </InternalLink>
                ))}
            </Box>
        </Box>
    );
};
